/**
 * mapIcons.ts
 *
 * Dedicated generator and registrar for custom MapLibre GL map icons.
 * Provides high-DPI SDF (Signed Distance Field) icons that dynamically adapt
 * to light and dark map themes with crisp halo casings.
 */

import type maplibregl from 'maplibre-gl';

// 4 vertical cascading streams with crest arcs
const WATERFALL_STREAMS = [
  "M 9.0 5.0 C 7.8 5.2 6.8 6.4 6.8 7.8 L 6.8 13.5",
  "M 12.2 4.2 C 11.0 4.4 10.0 5.4 10.0 6.8 L 10.0 13.5",
  "M 15.4 3.6 C 14.2 3.8 13.2 4.8 13.2 6.2 L 13.2 13.5",
  "M 18.6 3.2 C 17.4 3.4 16.4 4.4 16.4 5.8 L 16.4 13.5"
];

// 3 stacked undulating ripple waves
const WATERFALL_WAVES = [
  "M 4.5 16.5 Q 6.0 17.3 7.5 16.5 T 10.5 16.5 T 13.5 16.5 T 16.5 16.5 T 19.5 16.5",
  "M 4.5 19.0 Q 6.0 19.8 7.5 19.0 T 10.5 19.0 T 13.5 19.0 T 16.5 19.0 T 19.5 19.0",
  "M 4.5 21.5 Q 6.0 22.3 7.5 21.5 T 10.5 21.5 T 13.5 21.5 T 16.5 21.5 T 19.5 21.5"
];

/**
 * Generates an SDF ImageData mask for the waterfall icon at the requested pixel resolution.
 */
export function createWaterfallImageData(size = 32): ImageData {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, size, size);

  const scale = size / 24;
  ctx.scale(scale, scale);

  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.9;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  if (typeof Path2D !== 'undefined') {
    for (const stream of WATERFALL_STREAMS) {
      ctx.stroke(new Path2D(stream));
    }
    for (const wave of WATERFALL_WAVES) {
      ctx.stroke(new Path2D(wave));
    }
  }

  return ctx.getImageData(0, 0, size, size);
}

/**
 * Registers custom icons onto the MapLibre GL map instance, ensuring persistent
 * availability across style switches and initial load.
 */
export function registerMapIcons(map: maplibregl.Map): void {
  const addIcons = () => {
    try {
      if (!map.hasImage('waterfall-icon')) {
        map.addImage('waterfall-icon', createWaterfallImageData(32), { sdf: true });
      }
    } catch {
      // Ignore if map or context was destroyed
    }
  };

  // Immediate registration
  addIcons();

  // Re-register whenever a new style loads
  map.on('style.load', addIcons);

  // Fallback for on-demand icon resolution
  map.on('styleimagemissing', (e: any) => {
    if (e.id === 'waterfall-icon') {
      addIcons();
    }
  });
}
